import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AppDispatch, AppThunk } from "@/app/store";
import { fetchWeekSummary } from "@/api/fetchWeekSummary/fetchWeekSummaryApi";
import {
  WeekSummary,
  FetchWeekSummaryRequest,
  FetchWeekSummaryResponse,
} from "@/api/fetchWeekSummary/types";
import { getISOWeekNumber } from "@/utils/dateUtils";

interface WeekSummaryState {
  summaries: WeekSummary[];
  currentWeek: number | null;
  currentYear: number | null;
  loading: boolean;
  error: string | null;
}

const initialState: WeekSummaryState = {
  summaries: [],
  currentWeek: null,
  currentYear: null,
  loading: false,
  error: null,
};

const weekSummarySlice = createSlice({
  name: "weekSummary",
  initialState,
  reducers: {
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    setSummariesData: (
      state,
      action: PayloadAction<{
        summaries: WeekSummary[];
        currentWeek: number;
        currentYear: number;
      }>,
    ) => {
      state.summaries = action.payload.summaries;
      state.currentWeek = action.payload.currentWeek;
      state.currentYear = action.payload.currentYear;
    },
    resetWeekSummary: () => initialState,
  },
});

export const { setLoading, setError, setSummariesData, resetWeekSummary } =
  weekSummarySlice.actions;

export const fetchWeekSummaryAction =
  (requestData: FetchWeekSummaryRequest): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(setLoading(true));
    dispatch(setError(null));
    try {
      const response: FetchWeekSummaryResponse =
        await fetchWeekSummary(requestData);

      // Transform the nested response structure into a flat array
      const summaries: WeekSummary[] = [];

      // Get current week/year for reference
      const now = new Date();
      const currentYear = now.getFullYear();
      const currentWeek = getISOWeekNumber(now);

      // Iterate through years and weeks in the response
      Object.entries(response.data).forEach(([yearStr, weeks]) => {
        const year = parseInt(yearStr, 10);
        Object.entries(weeks).forEach(([weekStr, statusData]) => {
          const week = parseInt(weekStr, 10);

          // Just pass through the API data as-is
          summaries.push({
            week,
            year,
            ...statusData
          });
        });
      });

      dispatch(
        setSummariesData({
          summaries,
          currentWeek,
          currentYear,
        }),
      );
    } catch (error) {
      if (error instanceof Error) {
        dispatch(setError(error.message));
      } else {
        dispatch(setError("Failed to fetch week summary"));
      }
    } finally {
      dispatch(setLoading(false));
    }
  };

export default weekSummarySlice.reducer;
